import React from "react";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import FacilityNetworkBackButton from "./FacilityNetworkBackButton";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import {
  RHC_DASHBOARD_SUBTITLE,
  RHC_ROSTER_SUBTITLE,
  RHC_TYPE_CODE,
  buildLiveRhCDashboardModel,
  collectLinkedRhCFacilities,
  rhcToneColor,
  type RhcKpiTile,
  type RhcProviderBasedRow,
  type RhcRosterRow,
  type RhcTone,
} from "./facilityNetworkRhCDashboardUtils";
import {
  getNetworkRhc,
  getNetworkRhcForNetwork,
  type NetworkDashboardMeta,
  type RhcDashboardData,
} from "@services/networkDashboard";
import { FN } from "./facilityNetworkTheme";

type Props = {
  members: FacilityHierarchyInput[];
  selectedFacilityId: number | null;
  networkId?: number | null;
  onBack: () => void;
  onOpenFacility: (facility: MyFacilityListRow) => void;
};

const STATUS_OK = "#0b6a0b";
const STATUS_NEUTRAL = "#605e5c";
const VALUE_NEUTRAL = "#323130";

function ToneText({
  label,
  tone,
  fontWeight = 400,
}: {
  label: string;
  tone: RhcTone;
  fontWeight?: number;
}) {
  return (
    <Typography
      sx={{
        fontFamily: FN.fontFamily,
        fontSize: 14,
        fontWeight,
        lineHeight: "20px",
        color: rhcToneColor(tone),
      }}
    >
      {label}
    </Typography>
  );
}

function KpiTile({ tile }: { tile: RhcKpiTile }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: "100%", sm: "calc(50% - 4px)", md: 0 },
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: "4px",
        boxShadow:
          "1px 1px 2px rgba(98,102,104,0.05), -1px -1px 2px rgba(98,102,104,0.05)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: STATUS_NEUTRAL,
          lineHeight: "20px",
        }}
      >
        {tile.label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          color: tile.valueTone === "primary" ? FN.primary : VALUE_NEUTRAL,
          letterSpacing: FN.figmaTitleTracking,
          lineHeight: "32px",
        }}
      >
        {tile.value}
      </Typography>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 12,
          fontWeight: 400,
          color: STATUS_NEUTRAL,
          letterSpacing: "0.03px",
          lineHeight: "16px",
        }}
      >
        {tile.sublabel}
      </Typography>
    </Box>
  );
}

function RuralCell({ row }: { row: RhcRosterRow }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {row.ruralValid ? (
        <CheckIcon sx={{ fontSize: 14, color: STATUS_OK }} />
      ) : null}
      <ToneText label={row.ruralLabel} tone={row.ruralTone} />
    </Box>
  );
}

function RosterTable({
  rows,
  onOpenFacility,
}: {
  rows: RhcRosterRow[];
  onOpenFacility: (facility: MyFacilityListRow) => void;
}) {
  const headerSx = {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: STATUS_NEUTRAL,
    lineHeight: "20px",
    borderColor: "#e5e5e5",
    bgcolor: "#fafafa",
    py: 1,
    px: 1.5,
  };
  const cellSx = {
    fontFamily: FN.fontFamily,
    borderColor: "#e5e5e5",
    py: 1.5,
    px: 1.5,
    verticalAlign: "middle",
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, width: 270 }}>Facility</TableCell>
            <TableCell sx={headerSx}>Cost Report</TableCell>
            <TableCell sx={headerSx}>Productivity</TableCell>
            <TableCell sx={headerSx}>Air Status</TableCell>
            <TableCell sx={headerSx}>Rural Designation</TableCell>
            <TableCell sx={headerSx}>Next Filing</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              hover
              onClick={() => {
                if (row.dashboardFacility) onOpenFacility(row.dashboardFacility);
              }}
              sx={{
                cursor: row.dashboardFacility ? "pointer" : "default",
                "&:hover": { bgcolor: "#f3fdff" },
              }}
            >
              <TableCell sx={cellSx}>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 14,
                    fontWeight: 600,
                    color: VALUE_NEUTRAL,
                    lineHeight: "20px",
                  }}
                >
                  {row.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: STATUS_NEUTRAL,
                    letterSpacing: "0.03px",
                    lineHeight: "16px",
                  }}
                >
                  {row.metaLine}
                </Typography>
                {row.missingSourcesLabel ? (
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 11,
                      color: "#8b4409",
                      lineHeight: "16px",
                    }}
                  >
                    {row.missingSourcesLabel}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText label={row.costReportLabel} tone={row.costReportTone} />
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText
                  label={row.productivityLabel}
                  tone={row.productivityTone}
                />
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText label={row.airStatusLabel} tone={row.airStatusTone} />
              </TableCell>
              <TableCell sx={cellSx}>
                <RuralCell row={row} />
              </TableCell>
              <TableCell sx={cellSx}>
                <ToneText
                  label={row.nextFilingLabel}
                  tone={row.nextFilingTone}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function ProviderBasedSection({
  rows,
  totalRhC,
  onOpenFacility,
}: {
  rows: RhcProviderBasedRow[];
  totalRhC: number;
  onOpenFacility: (facility: MyFacilityListRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <FacilityNetworkFigmaCard padding={2} gap={0}>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            color: STATUS_NEUTRAL,
            lineHeight: "20px",
          }}
        >
          Provider-based RHCs — no provider-based clinics in your network
        </Typography>
      </FacilityNetworkFigmaCard>
    );
  }

  const capExposed = rows.filter((row) => row.airCapTone === "warn").length;
  const headerSx = {
    fontFamily: FN.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: STATUS_NEUTRAL,
    bgcolor: "#fafafa",
    borderColor: "#e5e5e5",
    py: 1,
    px: 1.5,
  };
  const cellSx = {
    fontFamily: FN.fontFamily,
    borderColor: "#e5e5e5",
    py: 1.5,
    px: 1.5,
  };

  return (
    <FacilityNetworkFigmaCard padding={0} gap={0}>
      <Box sx={{ p: 2, borderBottom: "1px solid #e5e5e5" }}>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            color: STATUS_NEUTRAL,
            lineHeight: "20px",
          }}
        >
          Provider-based clinics
        </Typography>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: STATUS_NEUTRAL,
            letterSpacing: "0.03px",
            lineHeight: "16px",
            mt: 0.5,
          }}
        >
          {rows.length} of {totalRhC} RHCs are provider-based · {capExposed}{" "}
          {capExposed === 1 ? "is" : "are"} AIR cap-exposed
        </Typography>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerSx}>Clinic</TableCell>
              <TableCell sx={headerSx}>Parent facility</TableCell>
              <TableCell sx={headerSx}>Parent beds</TableCell>
              <TableCell sx={headerSx}>AIR cap</TableCell>
              <TableCell sx={headerSx}>Cost per visit</TableCell>
              <TableCell sx={headerSx}>Provider-based since</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                hover
                onClick={() => {
                  if (row.dashboardFacility) onOpenFacility(row.dashboardFacility);
                }}
                sx={{
                  cursor: row.dashboardFacility ? "pointer" : "default",
                  "&:hover": { bgcolor: "#f3fdff" },
                }}
              >
                <TableCell sx={cellSx}>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 14,
                      fontWeight: 600,
                      color: VALUE_NEUTRAL,
                    }}
                  >
                    {row.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      color: STATUS_NEUTRAL,
                    }}
                  >
                    {row.metaLine}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography sx={{ fontSize: 14, color: FN.primary, fontWeight: 600 }}>
                    {row.parentFacilityName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                  {row.parentBedsLabel}
                </TableCell>
                <TableCell sx={cellSx}>
                  <ToneText label={row.airCapLabel} tone={row.airCapTone} />
                </TableCell>
                <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                  {row.costPerVisitLabel}
                </TableCell>
                <TableCell sx={cellSx}>{row.providerBasedSinceLabel}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </FacilityNetworkFigmaCard>
  );
}

type LiveLoadState =
  | { scopeKey: string; status: "idle" | "loading" }
  | { scopeKey: string; status: "loaded"; data: RhcDashboardData; meta: NetworkDashboardMeta }
  | { scopeKey: string; status: "error"; message: string };

/** Figma 9120:91639 — live RHC type dashboard via Rules GET /api/v1/network-dashboard/rhc */
export default function FacilityNetworkRhCDashboardView({
  members,
  selectedFacilityId,
  networkId = null,
  onBack,
  onOpenFacility,
}: Props) {
  const scopeKey =
    networkId != null
      ? `network:${networkId}`
      : selectedFacilityId != null
        ? `facility:${selectedFacilityId}`
        : "none";
  const linkedRhCs = collectLinkedRhCFacilities(members, selectedFacilityId);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [loadState, setLoadState] = React.useState<LiveLoadState>({
    scopeKey,
    status: "idle",
  });

  React.useEffect(() => {
    if (scopeKey === "none") {
      setLoadState({ scopeKey, status: "idle" });
      return;
    }
    let disposed = false;
    setLoadState({ scopeKey, status: "loading" });
    const request =
      networkId != null
        ? getNetworkRhcForNetwork(networkId, { limit: 500, offset: 0, sort: "name", direction: "asc" })
        : getNetworkRhc(selectedFacilityId!, { limit: 500, offset: 0, sort: "name", direction: "asc" });
    void request
      .then((response) => {
        if (!disposed) setLoadState({ scopeKey, status: "loaded", ...response });
      })
      .catch((error) => {
        if (!disposed) {
          setLoadState({
            scopeKey,
            status: "error",
            message: error instanceof Error ? error.message : "Failed to load RHC dashboard.",
          });
        }
      });
    return () => {
      disposed = true;
    };
  }, [networkId, reloadKey, scopeKey, selectedFacilityId]);

  const currentState =
    loadState.scopeKey === scopeKey
      ? loadState
      : ({ scopeKey, status: "loading" } as const);
  const model =
    currentState.status === "loaded"
      ? buildLiveRhCDashboardModel(currentState.data, linkedRhCs)
      : null;
  const qualityWarnings =
    currentState.status === "loaded"
      ? (currentState.meta.data_quality_warnings ?? []).filter(Boolean)
      : [];

  return (
    <Box
      translate="no"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      <FacilityNetworkBackButton
        label="Back to Networks Overview"
        onClick={onBack}
        variant="link"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: FN.figmaTitleSize,
              fontWeight: 600,
              color: FN.title,
              letterSpacing: FN.figmaTitleTracking,
              lineHeight: "32px",
            }}
          >
            Rural Health Clinics
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "24px",
              bgcolor: FN.badgeBg,
              boxShadow: "0px 2px 2px #e9f3f5",
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.title,
                lineHeight: "20px",
              }}
            >
              {model == null
                ? "Loading…"
                : `${model.facilityCount} Facilit${model.facilityCount === 1 ? "y" : "ies"}`}
            </Typography>
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: STATUS_NEUTRAL,
            letterSpacing: "0.03px",
            lineHeight: "16px",
          }}
        >
          {RHC_DASHBOARD_SUBTITLE}
        </Typography>
      </Box>

      {scopeKey === "none" ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            Select a facility or network to load RHC dashboard data.
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : currentState.status === "loading" || currentState.status === "idle" ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            Loading RHC dashboard…
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : currentState.status === "error" ? (
        <Box>
          <Typography component="div" sx={{ color: "#a4262c", fontSize: 14 }}>
            {currentState.message}
          </Typography>
          <Button size="small" onClick={() => setReloadKey((key) => key + 1)}>
            Retry
          </Button>
        </Box>
      ) : model && model.facilityCount === 0 && model.roster.length === 0 ? (
        <FacilityNetworkFigmaCard>
          <Typography sx={{ fontSize: 14, color: STATUS_NEUTRAL }}>
            No {RHC_TYPE_CODE}s in your network yet
          </Typography>
        </FacilityNetworkFigmaCard>
      ) : model ? (
        <>
          {qualityWarnings.length > 0 ? (
            <Alert severity="warning" icon={false}>
              <Box>
                {qualityWarnings.map((warning, index) => (
                  <Typography
                    key={`${index}-${warning}`}
                    component="div"
                    sx={{ fontSize: 12 }}
                  >
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : null}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              width: "100%",
            }}
          >
            {model.kpis.map((tile) => (
              <KpiTile key={tile.id} tile={tile} />
            ))}
          </Box>

          <FacilityNetworkFigmaCard padding={0} gap={0}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e5e5" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: STATUS_NEUTRAL,
                  lineHeight: "20px",
                }}
              >
                Facility roster
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: STATUS_NEUTRAL,
                  letterSpacing: "0.03px",
                  lineHeight: "16px",
                  mt: 0.5,
                }}
              >
                {RHC_ROSTER_SUBTITLE}
              </Typography>
            </Box>
            <RosterTable rows={model.roster} onOpenFacility={onOpenFacility} />
          </FacilityNetworkFigmaCard>

          <ProviderBasedSection
            rows={model.providerBased}
            totalRhC={model.facilityCount}
            onOpenFacility={onOpenFacility}
          />
        </>
      ) : null}
    </Box>
  );
}

export function isRhCTypeDashboard(typeCode: string | null | undefined): boolean {
  return String(typeCode || "").toUpperCase() === RHC_TYPE_CODE;
}
